import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FileText, IndianRupee, CheckCircle2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const GenerateReceiptModal = ({ payment, submission, open, onClose, onSuccess }) => {
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      await api.post(`/payments/${payment.payment_id}/generate-receipt`);
      toast.success('Receipt generated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate receipt');
    } finally {
      setGenerating(false);
    }
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">Generate Receipt</DialogTitle>
          <DialogDescription className="text-stone-500">
            Confirm payment and generate receipt for customer
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Payment Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center space-x-2 text-green-700 mb-2">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Payment Verified</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">Milestone</span>
              <span className="font-medium text-stone-900">{payment.stage_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600 flex items-center">
                <IndianRupee className="h-3.5 w-3.5 mr-1" />
                Amount Paid
              </span>
              <span className="text-xl font-bold text-green-700">
                ₹{(payment.paid_amount || payment.amount).toLocaleString('en-IN')}
              </span>
            </div>
            {payment.paid_date && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600 flex items-center">
                  <Calendar className="h-3.5 w-3.5 mr-1" />
                  Paid On
                </span>
                <span className="font-medium text-stone-900">
                  {new Date(payment.paid_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Receipt Preview */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <FileText className="h-6 w-6 text-stone-600" />
              <div>
                <p className="font-medium text-stone-900">Payment Receipt</p>
                <p className="text-sm text-stone-500">Will be sent to customer</p>
              </div>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">
              A formal receipt will be generated with the payment details and made available 
              for the customer to download.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
              disabled={generating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              disabled={generating}
              data-testid="generate-receipt-button"
            >
              {generating ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Generating...
                </span>
              ) : (
                <span className="flex items-center">
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Receipt
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateReceiptModal;
